import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ReviewReply } from "@/components/ReviewReply";
import { Star, MessageSquareReply } from "lucide-react";
import { toast } from "sonner";

function ReplyForm({ review, onPosted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (text.trim().length < 2) { toast.error("Write a short reply first."); return; }
    if (!window.confirm("Replies are public and can't be edited once posted. Post this reply?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reviews/${review.id}/reply`, { text: text.trim() });
      toast.success("Reply posted.");
      onPosted(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={300} rows={2}
        placeholder="Say thanks or respond publicly…" className="rounded-sm text-sm" data-testid={`reply-text-${review.id}`} />
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-muted-foreground">{text.length}/300 · Public, one reply per review, can't be edited.</p>
        <Button size="sm" className="rounded-sm" onClick={post} disabled={busy} data-testid={`reply-submit-${review.id}`}>
          <MessageSquareReply className="w-3.5 h-3.5 mr-1" />{busy ? "Posting…" : "Post reply"}
        </Button>
      </div>
    </div>
  );
}

export const ProviderReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/provider/reviews").then((r) => setReviews(r.data)).finally(() => setLoading(false)); }, []);
  const onPosted = (updated) => setReviews((list) => list.map((r) => (r.id === updated.id ? updated : r)));
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  if (loading) return <p className="text-muted-foreground">Loading…</p>;
  if (!reviews.length) return <p className="text-muted-foreground border border-dashed border-border rounded-sm p-8 text-center" data-testid="provider-reviews-empty">No reviews yet. Customers can review a booking once it's completed.</p>;
  return (
    <div data-testid="provider-reviews-tab">
      <p className="text-sm text-muted-foreground" data-testid="provider-reviews-summary">
        <span className="font-semibold text-foreground">{avg}★</span> average from {reviews.length} review{reviews.length > 1 ? "s" : ""}
      </p>
      <div className="mt-4 space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white border border-border rounded-sm p-5" data-testid={`provider-review-${r.id}`}>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
            </div>
            <p className="text-sm mt-2">“{r.comment}”</p>
            <p className="text-xs text-muted-foreground mt-1">{r.customer_name} · {r.service_title} · {new Date(r.created_at).toLocaleDateString()}</p>
            {r.reply ? (
              <>
                <ReviewReply reply={{ ...r.reply, hidden: false }} provider="you" className="mt-3" testid={`provider-review-reply-${r.id}`} />
                {r.reply.hidden && <p className="text-xs text-muted-foreground mt-1" data-testid={`provider-reply-hidden-${r.id}`}>This reply was hidden by BookdHUB and isn't shown publicly.</p>}
              </>
            ) : <ReplyForm review={r} onPosted={onPosted} />}
          </div>
        ))}
      </div>
    </div>
  );
};
